/**
 * =============================================================================
 * API CONFIGURATION - SINGLE SOURCE OF TRUTH
 * =============================================================================
 * 
 * ALL API URL LOGIC MUST BE IN THIS FILE AND NOWHERE ELSE.
 * 
 * If you need the API base URL, import from here:
 *   import { API_BASE_URL } from '../config/api';
 * 
 * DO NOT:
 * - Duplicate this logic in other files
 * - Use window.location.hostname to detect API URLs elsewhere
 * - Hardcode API URLs anywhere
 * 
 * =============================================================================
 * MAPPING:
 * =============================================================================
 *   Frontend Domain              →  API Domain
 *   ──────────────────────────────────────────────────
 *   hipotech.net                 →  https://api.hipotech.net
 *   www.hipotech.net             →  https://api.hipotech.net
 *   qa.hipotech.net              →  https://api-qa.hipotech.net
 *   dev.hipotech.net             →  https://api-dev.hipotech.net
 *   localhost / other            →  https://api-dev.hipotech.net
 * =============================================================================
 */

// Detect once at module load
const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';

let apiBaseUrl: string;
let environmentName: string;

if (hostname === 'hipotech.net' || hostname === 'www.hipotech.net') {
  apiBaseUrl = 'https://api.hipotech.net';
  environmentName = 'production';
} else if (hostname === 'qa.hipotech.net') {
  apiBaseUrl = 'https://api-qa.hipotech.net';
  environmentName = 'qa';
} else if (hostname === 'dev.hipotech.net') {
  apiBaseUrl = 'https://api-dev.hipotech.net';
  environmentName = 'development';
} else {
  // localhost, 127.0.0.1, or any other domain
  apiBaseUrl = 'https://api-dev.hipotech.net';
  environmentName = 'local';
}

/**
 * The API base URL - use this for all API calls
 */
export const API_BASE_URL: string = apiBaseUrl;

/**
 * The logical environment name derived from hostname.
 * Use this instead of process.env.NODE_ENV for display purposes.
 * NODE_ENV is always 'production' in any next build output, regardless of environment.
 */
export const ENVIRONMENT_NAME: string = environmentName;

/**
 * Build a full URL for an API endpoint
 * @example buildApiUrl('/member-area/status') → 'https://api.hipotech.net/member-area/status'
 */
export const buildApiUrl = (endpoint: string): string => {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE_URL}${path}`;
};

/**
 * Alias for backward compatibility
 */
export const getApiBaseUrl = (): string => API_BASE_URL;

// Log once at startup for debugging
console.log(`[api.ts] ${hostname} → ${API_BASE_URL}`);